import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function BlogPostPergolaPermit2() {
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Calgary Pergola Permit: Requirements, Cost & Guide",
        "description": "Learn Calgary pergola permit requirements, costs, application steps, and rules for attached pergolas and deck structures before building.",
        "author": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "url": "https://busybeaverconstruction.com"
        },
        "publisher": {
          "@type": "LocalBusiness",
          "name": "Busy Beaver Construction",
          "image": "https://busybeaverconstruction.com/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Calgary",
            "addressRegion": "AB",
            "addressCountry": "CA"
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Calgary Pergola Permit",
            "item": "https://busybeaverconstruction.com/blog/calgary-pergola-permit"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How long does a pergola permit take in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The City of Calgary lists an approximate review time of 7 days for a Building Permit. If your project also needs a Development Permit, the process may take 10–12 weeks, including the advertising and appeal period. Timelines can vary by application."
            }
          },
          {
            "@type": "Question",
            "name": "Do I need an electrical permit for a pergola in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You may need a separate electrical permit if you add lighting, outlets, or other electrical features. Check the City of Calgary’s homeowner electrical permit requirements before starting electrical work."
            }
          },
          {
            "@type": "Question",
            "name": "Should I get a permit before building a pergola?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. Confirm the permit requirements before construction begins. A pergola that needs a Building Permit must meet the applicable building and land-use rules. Starting work before approval can lead to delays or corrective work."
            }
          },
          {
            "@type": "Question",
            "name": "Do I need a permit for a pergola under 10 m² in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A freestanding pergola under 10 m² generally does not need a Building Permit if it meets the City’s applicable conditions. However, it must still comply with Calgary’s land-use rules, including applicable setback and height requirements. Check the City’s pergola and accessory-building requirements before building."
            }
          },
          {
            "@type": "Question",
            "name": "How much does a pergola permit cost in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The City lists a Building Permit fee of $205.92 for a new pergola. A Development Permit may cost an additional $550 if required. Confirm current fees with the City before applying."
            }
          },
          {
            "@type": "Question",
            "name": "Can I apply for a Calgary pergola permit online?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. You can submit an application through the City of Calgary’s online application portal. Prepare the required site plan, drawings, and supporting documents before you begin."
            }
          },
          {
            "@type": "Question",
            "name": "Does an attached pergola need a permit in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. Calgary treats a pergola attached to a house as an addition, which requires a Building Permit. Additional approvals may apply depending on the design and its compliance with land-use rules."
            }
          },
          {
            "@type": "Question",
            "name": "Does a pergola on a deck require a permit in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. The City requires a Building Permit for a covered deck, including one with a pergola or roof. Requirements can vary depending on whether the pergola attaches to the house or stands separately."
            }
          },
          {
            "@type": "Question",
            "name": "How close can a pergola be to my property line in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The required setback depends on the pergola’s size, location, and property conditions. A 0.6 m side or rear setback commonly applies to accessory residential buildings, but exceptions and additional rules may affect your project. Check the City’s requirements before choosing the location."
            }
          }
        ]
      }
    ]
  }
  
  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>Calgary Pergola Permit: Requirements, Cost & Guide</title>
        <meta name="description" content="Learn Calgary pergola permit requirements, costs, application steps, and rules for attached pergolas and deck structures before building." />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/calgary-pergola-permit" />
        <meta property="og:title" content="Calgary Pergola Permit: Requirements, Cost & Guide" />
        <meta property="og:description" content="Learn Calgary pergola permit requirements, costs, application steps, and rules for attached pergolas and deck structures before building." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://busybeaverconstruction.com/blog/calgary-pergola-permit" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />
      
      <section className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img 
          src="/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg"
          alt="Modern cedar pergola construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-32 text-center max-w-4xl mx-auto px-4">
          <div className="inline-block bg-beaver-green rounded py-2 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Permit Guide</p>
          </div>
          <h1 className="text-white text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight">
            Calgary Pergola Permit Requirements
          </h1>
          <p className="text-white/90 text-lg font-medium font-accent mt-4">
            A Complete Guide to Requirements, Costs, and Application Steps
          </p>
        </div>
      </section>
      
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 relative">
            
            <article className="lg:col-span-8 space-y-8 text-lg text-gray-700 leading-relaxed">
              
              {/* Table of Contents */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4 font-secondary">Table of Contents</h3>
                <ul className="space-y-3 font-medium">
                  <li><a href="#need-permit" className="text-beaver-green hover:underline">Do You Need a Permit for a Pergola in Calgary?</a></li>
                  <li><a href="#requirements" className="text-beaver-green hover:underline">Calgary Pergola Permit Requirements</a></li>
                  <li><a href="#on-deck" className="text-beaver-green hover:underline">Does a Pergola on a Deck Need a Permit?</a></li>
                  <li><a href="#cost" className="text-beaver-green hover:underline">How Much Does a Pergola Permit Cost in Calgary?</a></li>
                  <li><a href="#apply" className="text-beaver-green hover:underline">How to Apply for a Calgary Pergola Permit Online</a></li>
                  <li><a href="#faq" className="text-beaver-green hover:underline">Frequently Asked Questions</a></li>
                </ul>
              </div>

              <p className="text-xl text-gray-900 leading-relaxed font-bold">
                Planning a pergola in Calgary? Before construction, check whether your design needs a permit. Requirements depend on the pergola’s size, location, and whether it attaches to your home or sits on a deck.
              </p>
              <p>
                This guide covers Calgary pergola permit requirements, costs, application steps, and timelines to help you plan your project.
              </p>
              
              <h2 id="need-permit" className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">Do You Need a Permit for a Pergola in Calgary?</h2>
              <p>
                A <strong>Calgary pergola permit</strong> depends mainly on the structure's size, location, and whether you attach it to your home or add it to a deck. A freestanding pergola over <strong>10 m² (107 sq. ft.)</strong> requires a building permit. A smaller freestanding pergola typically does not, but it must still follow Calgary's land-use rules.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Freestanding Pergola Over 10 m²</h3>
              <p>
                A freestanding pergola larger than <strong>10 m² (107 sq. ft.)</strong> requires a building permit. The City of Calgary classifies a pergola of this type as an <strong>accessory residential building</strong>, along with structures such as gazebos, sheds and greenhouses.
              </p>
              <p>
                When you apply, the City may require a site plan, drawings and other project-specific documents. Your plans should clearly show the pergola's <strong>dimensions, location, height and clearances from property lines</strong>. The City currently lists a <strong>$205.92 building permit fee</strong> for a new pergola, including the 4% Safety Codes Council fee.
              </p>
              <p>
                If your proposed location does not meet the applicable <strong>Calgary Land Use Bylaw 1P2007</strong> rules, you may also need a Development Permit. That approval is separate from the building permit.
              </p>
              <p>
                If you're planning a larger custom structure, you can also explore <a href="/services/pergolas-and-privacy-walls" onClick={(e) => handleNavigation(e, '/services/pergolas-and-privacy-walls')} className="text-beaver-green font-bold hover:underline">Calgary pergola builders</a> to see the available pergola service.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Freestanding Pergola Under 10 m²</h3>
              <p>
                A freestanding pergola <strong>under 10 m² (107 sq. ft.) typically does not require a building permit</strong>. However, the exemption does not remove the need to follow Calgary's land-use and design requirements.
              </p>
              <p>
                For example, the City does not allow an accessory residential building such as a pergola in the actual front setback. Standard property-line clearances can also apply. A <strong>0.6 m (1.9 ft.)</strong> setback generally applies to a side or rear property line shared with another property, while a <strong>1.2 m (3.9 ft.)</strong> setback applies where the side property line is shared with a street. Specific exceptions can reduce these setbacks when the structure meets the City's conditions.
              </p>
              <p>
                Height matters too. Calgary limits an accessory residential building to <strong>4.6 m (15 ft.)</strong> at its highest point and <strong>3.0 m (9.8 ft.)</strong> at the highest eaveline. The finished floor generally cannot sit more than <strong>0.6 m (1.9 ft.) above grade</strong>.
              </p>
              <p>
                This last rule is particularly important for elevated outdoor structures. The City specifically notes that a pergola built on a deck higher than <strong>0.6 m above grade</strong> would not meet the bylaw and would require a Development Permit.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Pergola Attached to Your House</h3>
              <p>
                An attached pergola requires a <strong>building permit regardless of its size</strong>. Calgary treats a structure attached to the house as an <strong>addition</strong>, so the under-10-m² exemption does not apply.
              </p>
              <p>
                The same distinction applies when you add a pergola to a deck. The City requires a building permit for a <strong>covered deck</strong>, which includes a deck with a pergola or roof. If the pergola or roof attaches to the house, Calgary treats it as an addition. If it does not attach to the house, the City classifies it as an accessory residential building.
              </p>
              <p>
                Planning a pergola and deck together? Our guide to <a href="/blog/4" onClick={(e) => handleNavigation(e, '/blog/4')} className="text-beaver-green font-bold hover:underline">deck permit requirements in Calgary</a> explains the related deck permit rules.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-beaver-green my-6">
                <p className="mb-0 text-gray-700">
                  <strong>Bottom line:</strong> Don't use the <strong>10 m² threshold</strong> as the only test. Check the pergola's size, attachment, height, location and whether it forms part of a covered deck before you start construction.
                </p>
              </div>

              <h2 id="requirements" className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">Calgary Pergola Permit Requirements</h2>
              <p>
                Calgary does not use “pergola” as a separate permit category. The City determines the applicable rules based on the structure's size, attachment, location, and relationship to a deck or home.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Freestanding Pergola Under 10 m² (107 sq. ft.)</h3>
              <p>
                A ground-level freestanding pergola with a footprint of <strong>10 m² (107 sq. ft.) or less</strong> generally does not require a Building Permit. However, the exemption only applies to the building permit requirement. The structure must still comply with <strong>Calgary Land Use Bylaw 1P2007</strong>.
              </p>
              <p>Key requirements include:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-beaver-green">
                <li><strong>Front setback:</strong> The pergola cannot sit within the actual front setback area.</li>
                <li><strong>Side and rear setbacks:</strong> A <strong>0.6 m (1.9 ft.)</strong> clearance generally applies from interior side and rear property lines.</li>
                <li><strong>Zero setback:</strong> Calgary allows a zero-metre setback in certain situations when the structure meets conditions for size, maintenance-free exterior materials, eaves, and roof drainage.</li>
                <li><strong>Corner lots:</strong> A <strong>1.2 m (3.9 ft.)</strong> setback applies along a side property line shared with a street.</li>
                <li><strong>Height:</strong> The maximum overall height is <strong>4.6 m (15 ft.)</strong>.</li>
              </ul>
              <p>
                These rules matter even when a building permit is not required. Before placing a pergola close to a property line, check the applicable land-use requirements for the property.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Freestanding Pergola Over 10 m² (107 sq. ft.)</h3>
              <p>
                A freestanding pergola larger than <strong>10 m² (107 sq. ft.)</strong> requires a <strong>Building Permit</strong> before construction begins. Calgary treats this type of structure as an <strong>accessory residential building</strong>, similar to a detached garage, shed, gazebo, or greenhouse.
              </p>
              <p>The permit application can require:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-beaver-green">
                <li>A dimensioned site plan</li>
                <li>Structural drawings, including elevations and cross-sections</li>
                <li>An Accessory Building Checklist</li>
                <li>A Public Tree Disclosure Statement where applicable</li>
              </ul>
              <p>
                The current listed Building Permit fee for a new pergola is <strong>$205.92</strong>, which includes the $198 permit fee and $7.92 Safety Codes Council fee. The City's listed review timeline for this type of application is approximately <strong>7 days</strong>.
              </p>
              <p>
                For larger accessory structures, Calgary also requires at least <strong>1.0 m (3.2 ft.) of separation</strong> from the main residential building.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Pergola Attached to Your House or Deck</h3>
              <p>An attached pergola follows different rules from a small freestanding structure.</p>
              <ul className="list-disc pl-6 space-y-4 mb-6 marker:text-beaver-green">
                <li><strong>Attached to a house:</strong> When you structurally attach a pergola to a house or attached garage, Calgary treats the project as an <strong>addition</strong>. The structure therefore requires a Building Permit regardless of its size.</li>
                <li><strong>Built on a deck:</strong> A pergola or roof added to a deck creates a <strong>covered deck</strong>. Calgary requires a Building Permit for covered decks. If the roof or pergola attaches to the house, the City treats the work as an addition. If it remains separate from the house, the structure falls under the accessory residential building rules.</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Planning Approval, Engineering and Site Restrictions</h3>
              <p>
                A Building Permit does not replace Calgary's land-use requirements. If your pergola does not meet applicable <strong>Land Use Bylaw 1P2007</strong> rules for location, setbacks, or height, you may need a <strong>Development Permit</strong> before proceeding.
              </p>
              <p>
                The listed Development Permit fee for this type of residential project is <strong>$550</strong>. Projects that require planning approval can also take considerably longer than a standard building permit review, with some approvals taking <strong>10 to 12 weeks</strong>.
              </p>
              <p>Other site conditions can affect the project as well:</p>
              <ul className="list-disc pl-6 space-y-4 mb-6 marker:text-beaver-green">
                <li><strong>Foundations:</strong> Attached pergolas and structures on elevated platforms may require foundations designed for Calgary's frost conditions. The project may also require professional engineering where the foundation or structural design falls outside standard solutions.</li>
                <li><strong>Public trees:</strong> Ground disturbance near a public tree can trigger additional tree-protection requirements. A <strong>Public Tree Disclosure Statement</strong> and other documentation may apply depending on the project location.</li>
                <li><strong>Utilities:</strong> Before excavation, confirm the location of underground utilities through <strong>Click Before You Dig</strong>. Electrical lighting, radiant heaters, or other trade work can also require separate permits.</li>
              </ul>
              
              <h2 id="on-deck" className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">Does a Pergola on a Deck Need a Permit?</h2>
              <p>
                <strong>Yes. A pergola added to a deck in Calgary requires a Building Permit.</strong> The City treats a deck with a pergola or roof as a <strong>covered deck</strong>, so the exemption that can apply to certain low, uncovered decks does not apply.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Why a Pergola on a Deck Requires a Permit</h3>
              <p>
                The main difference is the overhead structure. A basic uncovered deck can qualify for a permit exemption when it meets Calgary's applicable height and other requirements. Adding a pergola changes the deck into a covered structure.
              </p>
              <p>
                If the pergola attaches to the house, Calgary treats the work as an <strong>addition</strong>. If it remains separate from the house, the project falls under the rules for an <strong>accessory residential building</strong>.
              </p>
              <p>
                The existing deck also needs to support the new structure safely. Adding pergola posts, beams and overhead members can change the loads carried by the deck's <strong>joists, beams and footings</strong>.
              </p>
              <p>
                For this reason, don't assume an existing deck is automatically suitable for a pergola. The proposed connection and supporting structure should form part of the project review.
              </p>

              <h2 id="cost" className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">How Much Does a Pergola Permit Cost in Calgary?</h2>
              <p>
                The Calgary pergola permit cost starts at $205.92 for projects that require a Building Permit. You may pay additional fees for a Development Permit or separate trade permits, depending on your pergola's design and the work involved.
              </p>

              <div className="overflow-x-auto my-8 shadow-sm rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="p-4 font-bold font-secondary">Permit type</th>
                      <th className="p-4 font-bold font-secondary">When it applies</th>
                      <th className="p-4 font-bold font-secondary">Listed fee</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="bg-white hover:bg-gray-50 transition-colors">
                      <td className="p-4 font-medium text-gray-900">Building Permit</td>
                      <td className="p-4 text-gray-600">Pergolas over 10 m², attached pergolas, or pergolas on decks</td>
                      <td className="p-4 font-bold text-beaver-green">$205.92</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-medium text-gray-900">Development Permit</td>
                      <td className="p-4 text-gray-600">When the project requires additional land-use approval</td>
                      <td className="p-4 font-bold text-beaver-green">$550.00</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50 transition-colors">
                      <td className="p-4 font-medium text-gray-900">Homeowner Electrical Permit</td>
                      <td className="p-4 text-gray-600">For applicable electrical work</td>
                      <td className="p-4 font-bold text-beaver-green">$116.50</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-medium text-gray-900">Homeowner Plumbing Permit</td>
                      <td className="p-4 text-gray-600">For applicable plumbing work</td>
                      <td className="p-4 font-bold text-beaver-green">$116.50</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Building Permit: $205.92</h3>
              <p>
                The City of Calgary lists the pergola building permit fee as $198.00 plus a $7.92 Safety Codes Council fee, for a total of $205.92. This is the listed fee for a new pergola that requires this permit category.
              </p>
              <p>
                A freestanding pergola measuring 10 m² (107 sq. ft.) or less may qualify for a building permit exemption if it meets the applicable conditions. In that case, you do not pay this Building Permit fee. The exemption does not remove land-use requirements.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Development Permit: $550</h3>
              <p>
                If your proposed pergola needs a relaxation of Calgary's Land Use Bylaw rules, the City lists a $550 Development Permit fee for the relevant accessory-building application. The breakdown is:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-beaver-green">
                <li>$365 application fee</li>
                <li>$30 public advertising fee</li>
                <li>$155 development completion inspection fee</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Does the Permit Fee Include Construction?</h3>
              <p>
                No. Permit fees cover municipal approvals, not the cost of building the pergola. Materials, labour, foundation work, engineering and electrical or gas installation can add to your project budget.
              </p>

              <h2 id="apply" className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">How to Apply for a Calgary Pergola Permit Online</h2>
              <p>
                You can apply for a pergola permit through the City of Calgary's online services portal. Before you begin, confirm which permit category applies to your project and prepare the required plans and supporting documents.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 1: Create a myID Account</h3>
              <p className="mb-4">Visit apply.calgary.ca and sign in with your City of Calgary myID account.</p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 2: Prepare Your Site Plan and Drawings</h3>
              <p className="mb-4">Depending on your project, you may need:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-beaver-green">
                <li>A dimensioned site plan showing property lines, existing structures, the proposed pergola location and distances from property boundaries.</li>
                <li>Structural drawings showing the pergola's dimensions, height, posts, beams and other construction details.</li>
                <li>A completed checklist or worksheet specified by the City for your permit category.</li>
                <li>Additional documents, such as a Public Tree Disclosure Statement, right-of-way agreement or engineer-stamped drawings, if the project requires them.</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 3: Choose the Correct Application Category</h3>
              <p className="mb-4">The application category depends on how your pergola relates to your house and deck:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-beaver-green">
                <li><strong>Freestanding pergola over 10 m² (107 sq. ft.):</strong> Follow the requirements for an accessory residential building, such as a shed or gazebo.</li>
                <li><strong>Pergola attached to your house:</strong> Follow the requirements for a residential addition.</li>
                <li><strong>Pergola added to a deck:</strong> Follow the City's covered-deck requirements.</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 4: Pay the Permit Fees</h3>
              <p className="mb-4">
                The City requires full payment by credit card when you submit an online application.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 5: Track Your Application</h3>
              <p className="mb-4">
                After you submit your application, the City sends a confirmation email containing a Job Access Code (JAC). Use that code on the City of Calgary VISTA portal to check your application's status.
              </p>

              <div className="bg-gray-900 text-white rounded-xl p-8 my-12 shadow-xl border-l-4 border-beaver-green">
                <h2 className="text-2xl font-bold mb-4 font-secondary text-beaver-green">Planning a Pergola in Calgary?</h2>
                <p className="mb-4 text-gray-300">
                  Planning a pergola starts with understanding the permit requirements for your property. The rules can depend on the structure’s size, location, attachment to your home, and deck configuration. Confirm the requirements before construction to avoid delays and unexpected costs.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="inline-flex justify-center items-center px-6 py-3 bg-beaver-green text-black font-bold rounded shadow-lg hover:brightness-110 transition-all duration-300 font-accent">
                    Request an Estimate
                  </a>
                </div>
              </div>

              <h2 id="faq" className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">How long does a pergola permit take in Calgary?</h4>
                  <p>The City of Calgary lists an approximate review time of 7 days for a Building Permit. If your project also needs a Development Permit, the process may take 10–12 weeks, including the advertising and appeal period. Timelines can vary by application.</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Do I need an electrical permit for a pergola in Calgary?</h4>
                  <p>You may need a separate electrical permit if you add lighting, outlets, or other electrical features.</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Should I get a permit before building a pergola?</h4>
                  <p>Yes. Confirm the permit requirements before construction begins. A pergola that needs a Building Permit must meet the applicable building and land-use rules. Starting work before approval can lead to delays or corrective work.</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Do I need a permit for a pergola under 10 m² in Calgary?</h4>
                  <p>A freestanding pergola under 10 m² generally does not need a Building Permit if it meets the City’s applicable conditions. However, it must still comply with Calgary’s land-use rules, including applicable setback and height requirements.</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Does an attached pergola need a permit in Calgary?</h4>
                  <p>Yes. Calgary treats a pergola attached to a house as an addition, which requires a Building Permit. Additional approvals may apply depending on the design and its compliance with land-use rules.</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">How close can a pergola be to my property line in Calgary?</h4>
                  <p>The required setback depends on the pergola’s size, location, and property conditions. A 0.6 m side or rear setback commonly applies to accessory residential buildings, but exceptions and additional rules may affect your project.</p>
                </div>
              </div>

            </article>
            
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold mb-6 font-secondary text-gray-900">Project Inspiration</h3>
                
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
                  {[
                    "/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg",
                    "/assets/images/Pergola/Cedar-Gazebo-with-shingled-roof.jpg",
                    "/assets/images/Privacy Walls/Cedar-Horiztonal-slat-privacy-wall-with-Cedar-Arbor-top.jpg",
                    "/assets/images/Pergola/Modern-Pergola-design-for-outdoor-seating-area.jpg",
                    "/assets/images/Decks/Wood Decks/Large-outdoor-seating-area-with-Custom-Cedar-privacy-wall.jpg"
                  ].map((src, index) => (
                    <div key={index} className="overflow-hidden rounded-lg h-48 lg:h-56 group cursor-pointer shadow-sm">
                      <img 
                        src={src}
                        alt={`Inspiration gallery image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 loading-lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
  )
}